import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "@/store/user";
import { api } from "@/lib/api";
import { useStats } from "@/lib/useStats";
import usePageTitle from "@/hooks/usePageTitle";
import StoreBadges from "@/components/StoreBadges";
import { LEGAL, LEGAL_LINKS } from "@/lib/legal";
import { isNativeApp, isIOS, openManageSubscription } from "@/lib/platform";
import * as RC from "@/lib/revenuecat";

const FAQ = [
  {
    q: "ما الفرق بين الخطّة المجّانيّة و Premium؟",
    a: "المجّانيّة تَفتح لك مسار «بَلاغة 1 (A1)» كاملًا مع شهادته. Premium يَفتح كلّ المسارات المدفوعة (A2 → CLS) بكامل محتواها من دروس واختبارات وشهادات.",
  },
  {
    q: "ما وسائل الدفع المتاحة؟",
    a: "الاشتراكات تتمّ حصريًّا عبر App Store لأجهزة Apple و Google Play لأجهزة Android — بأمانٍ تامٍّ عبر نظام IAP القياسيّ للمتاجر، مع دعم كلّ وسائل الدفع المعتمدة لدى Apple و Google.",
  },
  {
    q: "هل الاشتراك يتجدّد تلقائيًّا؟",
    a: "نعم. جميع الاشتراكات تتجدّد تلقائيًّا في نهاية كلّ فترة (شهر أو سنة) ما لم تُلغِها قبل 24 ساعة من انتهاء الفترة الحاليّة. يمكنك الإلغاء في أيّ وقتٍ من إعدادات الاشتراكات في جهازك.",
  },
  {
    q: "كيف أُلغي الاشتراك؟",
    a: "من داخل التطبيق: «حسابي ← إدارة الاشتراك». أو مباشرةً على iOS: Settings ← Apple ID ← Subscriptions. وعلى Android: Play Store ← Payments & subscriptions ← Subscriptions.",
  },
  {
    q: "ماذا يحدث للشهادات إذا ألغيت Premium؟",
    a: "كلّ شهادةٍ حصلت عليها تبقى لك للأبد — يمكنك إعادة تنزيلها متى شئت، حتى بعد الإلغاء.",
  },
  {
    q: "هل مسار «همزة» داخل Premium؟",
    a: "لا — مسار «همزة» منتجٌ مستقلٌّ متخصّصٌ في الاستعداد لاختبار همزة، وله اشتراكٌ منفصلٌ سيُطلَق قريبًا.",
  },
];

export default function PricingPage() {
  const navigate = useNavigate();
  usePageTitle("الأسعار والاشتراكات · بَلاغة");
  const { isAuthed, subInfo, refreshSubscription } = useUser();
  const stats = useStats();
  const [billing, setBilling] = useState("yearly"); // monthly | yearly
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    refreshSubscription();
    // On native, boot the RevenueCat SDK and identify the current user.
    // On web this is a no-op (the SDK returns false immediately).
    if (isNativeApp) {
      RC.init(subInfo?.user_id || null).catch((e) =>
        console.warn("RC init failed", e)
      );
    }
  }, [refreshSubscription, subInfo?.user_id]);

  const plans = subInfo?.plans || [];
  const monthly = useMemo(
    () => plans.find((p) => p.id === "monthly") || { amount_sar: 29 },
    [plans]
  );
  const yearly = useMemo(
    () => plans.find((p) => p.id === "yearly") || { amount_sar: 249 },
    [plans]
  );
  const premiumActive = !!subInfo?.subscription?.active;
  const pilotMode = !!subInfo?.pilot_mode;

  const handleSubscribe = async () => {
    setErr("");
    if (!isAuthed) {
      navigate("/login?from=pricing");
      return;
    }
    // On native iOS/Android, purchases go through RevenueCat → StoreKit / Play Billing.
    if (isNativeApp) {
      setBusy(true);
      try {
        await RC.init(subInfo?.user_id || null);
        const packages = await RC.getOfferings();
        // Match by product identifier (e.g. "com.balagha.app.core.monthly")
        const targetId =
          billing === "monthly"
            ? "com.balagha.app.core.monthly"
            : "com.balagha.app.core.yearly";
        const pkg = packages.find(
          (p) => p.product?.identifier === targetId || p.identifier === targetId
        );
        if (!pkg) {
          setErr(
            "لم نتمكّن من العثور على خطّة الاشتراك في المتجر. تأكّد من إعداد المنتجات في App Store Connect / Play Console."
          );
          return;
        }
        await RC.purchasePackage(pkg);
        // RevenueCat webhook will update our backend; refresh from server.
        await refreshSubscription();
        navigate("/tracks");
      } catch (e) {
        // User-cancelled purchases throw with code PURCHASE_CANCELLED.
        if (e?.code === "PURCHASE_CANCELLED" || /cancel/i.test(e?.message || "")) {
          setErr("");
        } else {
          setErr(
            e?.userInfo?.readableErrorCode ||
              e?.message ||
              "تعذّرت عمليّة الشراء عبر المتجر."
          );
        }
      } finally {
        setBusy(false);
      }
      return;
    }
    // Web fallback (dev / preview) — kept for local QA only.
    setBusy(true);
    try {
      const session = await api.createCheckout(billing);
      if (session.mode === "mock") {
        navigate(session.checkout_url);
      } else if (session.mode === "live") {
        navigate(`/checkout/mock/${session.payment_id}`);
      }
    } catch (e) {
      setErr(
        e?.response?.data?.detail ||
          "تعذّر بدء عمليّة الدفع. تأكّد من تسجيل الدخول وحاول مجدّدًا."
      );
    } finally {
      setBusy(false);
    }
  };

  const handleRestorePurchases = async () => {
    setErr("");
    if (!isAuthed) {
      navigate("/login?from=pricing");
      return;
    }
    setBusy(true);
    try {
      if (isNativeApp) {
        await RC.init(subInfo?.user_id || null);
        await RC.restore();
      }
      // Whether web or native, poke the backend to pull the latest entitlements.
      if (typeof api.restorePurchases === "function") {
        await api.restorePurchases();
      }
      await refreshSubscription();
      const ok = !!subInfo?.subscription?.active;
      setErr(
        ok
          ? ""
          : "لم نجد اشتراكًا نشطًا مرتبطًا بحسابك في متجر جهازك. تحقّق من أنّك سجّلت الدخول بنفس حساب Apple/Google الذي اشتريت به."
      );
    } catch (e) {
      setErr(
        e?.response?.data?.detail || e?.message || "تعذّر استرجاع المشتريات. حاول مجدّدًا لاحقًا."
      );
    } finally {
      setBusy(false);
    }
  };

  const premium = billing === "monthly" ? monthly : yearly;
  const monthlyEquivalent =
    billing === "yearly"
      ? Math.round((yearly.amount_sar / 12) * 10) / 10
      : monthly.amount_sar;

  return (
    <div
      className="shell has-bottom-nav"
      data-testid="pricing-page"
      style={{ paddingBottom: 140 }}
    >
      {/* Hero */}
      <section style={{ textAlign: "center", padding: "60px 16px 30px" }}>
        <h1 style={{ fontSize: "clamp(28px, 5vw, 44px)", marginBottom: 12 }}>
          خطّةٌ تَلِيق بطموحك في البلاغة ✨
        </h1>
        <p
          className="muted"
          style={{
            maxWidth: 660,
            margin: "0 auto",
            lineHeight: 1.9,
            fontSize: 17,
          }}
        >
          ابدأ مجّانًا بمسار «بَلاغة 1»، أو افتح المنهج الكامل ({stats.tracks} مسارات، {stats.lessons}
          {" "}درسًا، {stats.certificates} شهادات) باشتراك Premium. الاشتراكات متوفّرةٌ عبر
          App Store و Google Play عند إطلاق التطبيق الأصليّ.
        </p>

        {/* Official store badges (coming soon) */}
        <StoreBadges align="center" />

        {premiumActive && (
          <div
            data-testid="active-sub-banner"
            style={{
              maxWidth: 480,
              margin: "20px auto 0",
              padding: "12px 16px",
              background: "rgba(46,196,142,0.12)",
              border: "1px solid #2ec48e55",
              borderRadius: 12,
              color: "#2ec48e",
              fontWeight: 600,
            }}
          >
            ✓ اشتراك Premium نشطٌ على حسابك — استمتع بكلّ المسارات!
          </div>
        )}

        {pilotMode && (
          <div
            data-testid="pilot-banner"
            style={{
              maxWidth: 640,
              margin: "20px auto 0",
              padding: "16px 20px",
              background: "rgba(199,156,106,0.10)",
              border: "1px solid #c79c6a55",
              borderRadius: 14,
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 22, marginBottom: 6 }}>🎁</div>
            <strong style={{ color: "#c79c6a", fontSize: 16 }}>
              إصدارٌ تجريبيٌّ مفتوحٌ للجميع
            </strong>
            <p
              className="muted"
              style={{
                margin: "8px 0 0",
                fontSize: 13.5,
                lineHeight: 1.85,
              }}
            >
              في مرحلة الإطلاق التجريبيِّ، جميع الـ {stats.lessons} درسًا والـ {stats.tests} اختبارًا
              والـ {stats.certificates} شهادات مفتوحةٌ مجّانًا لكلِّ المستخدمين. نَستكشف معكم
              ونُحسِّن. الاشتراكات ستُفعَّل في تحديثٍ قريب.
            </p>
          </div>
        )}
      </section>

      {/* Billing toggle */}
      <section
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 4,
          marginBottom: 28,
        }}
      >
        <div
          style={{
            background: "#1a1530",
            border: "1px solid #2a2640",
            borderRadius: 100,
            padding: 4,
            display: "inline-flex",
          }}
        >
          <button
            data-testid="billing-monthly"
            onClick={() => setBilling("monthly")}
            style={{
              padding: "8px 18px",
              borderRadius: 100,
              border: "none",
              background: billing === "monthly" ? "#c79c6a" : "transparent",
              color: billing === "monthly" ? "#1a1530" : "#e9e6ff",
              fontWeight: 600,
              cursor: "pointer",
              fontSize: 14,
            }}
          >
            شهريّ
          </button>
          <button
            data-testid="billing-yearly"
            onClick={() => setBilling("yearly")}
            style={{
              padding: "8px 18px",
              borderRadius: 100,
              border: "none",
              background: billing === "yearly" ? "#c79c6a" : "transparent",
              color: billing === "yearly" ? "#1a1530" : "#e9e6ff",
              fontWeight: 600,
              cursor: "pointer",
              fontSize: 14,
            }}
          >
            سنويّ
            <span
              style={{
                marginInlineStart: 6,
                background: "#2ec48e",
                color: "#0e1430",
                padding: "2px 6px",
                borderRadius: 100,
                fontSize: 11,
              }}
            >
              وفّر ٢٨٪
            </span>
          </button>
        </div>
      </section>

      {/* Tiers */}
      <section
        className="pricing-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 20,
          margin: "10px auto",
          maxWidth: 1100,
          padding: "0 16px",
        }}
      >
        {/* Free */}
        <div
          data-testid="pricing-card-free"
          className="panel"
          style={{
            border: "1px solid #2a2640",
            padding: 28,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              color: "#2ec48e",
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: 1,
              marginBottom: 8,
            }}
          >
            المجّاني
          </div>
          <h3 style={{ fontSize: 22, marginBottom: 18 }}>ابدأ مسيرتك</h3>
          <div style={{ marginBottom: 24 }}>
            <span style={{ fontSize: 42, fontWeight: 700 }}>0 ر.س</span>
            <span className="muted" style={{ fontSize: 14, marginInlineStart: 6 }}>
              / للأبد
            </span>
          </div>
          <ul style={{ listStyle: "none", padding: 0, marginBottom: 22, flex: 1 }}>
            {[
              "وصولٌ كاملٌ لمسار «بَلاغة 1» (A1) — 8 دروس",
              "اختبارات A1 + شهادة PDF",
              "نظام XP، الشارات، والمتابعة اليوميّة",
              "تطبيق PWA على الجوّال",
            ].map((f, i) => (
              <li
                key={i}
                data-testid={`pricing-feature-free-${i}`}
                style={{
                  display: "flex",
                  gap: 10,
                  marginBottom: 10,
                  fontSize: 14,
                  lineHeight: 1.6,
                }}
              >
                <span style={{ color: "#2ec48e", fontSize: 16 }}>✓</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <button
            className="btn btn-ghost"
            onClick={() => navigate("/tracks")}
            data-testid="pricing-cta-free"
            style={{
              border: "1px solid #2ec48e",
              color: "#2ec48e",
              fontWeight: 600,
              padding: 12,
            }}
          >
            ابدأ مجّانًا الآن
          </button>
        </div>

        {/* Premium */}
        <div
          data-testid="pricing-card-premium"
          className="panel"
          style={{
            border: "2px solid #c79c6a",
            padding: 28,
            position: "relative",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 10px 40px #c79c6a33",
            transform: "translateY(-8px)",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: -12,
              right: 24,
              background: "#c79c6a",
              color: "#1a1530",
              padding: "4px 14px",
              borderRadius: 100,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.5,
            }}
            data-testid="pricing-badge-premium"
          >
            الأكثر اختيارًا
          </div>

          <div
            style={{
              color: "#c79c6a",
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: 1,
              marginBottom: 8,
            }}
          >
            💎 Balagha Core
          </div>
          <h3 style={{ fontSize: 22, marginBottom: 18 }}>الحزمة الأساس</h3>

          <div style={{ marginBottom: 18 }}>
            <span style={{ fontSize: 42, fontWeight: 700 }}>
              {premium.amount_sar} ر.س
            </span>
            <span
              className="muted"
              style={{ fontSize: 14, marginInlineStart: 6 }}
            >
              / {billing === "monthly" ? "شهر" : "سنة"}
            </span>
            {billing === "yearly" && (
              <div
                className="muted"
                style={{ fontSize: 13, marginTop: 6, lineHeight: 1.7 }}
              >
                ما يعادل ≈ {monthlyEquivalent} ر.س / شهر — وفّر شهرين تقريبًا.
              </div>
            )}
          </div>

          <ul
            style={{
              listStyle: "none",
              padding: 0,
              marginBottom: 22,
              flex: 1,
            }}
          >
            {[
              "كلّ ما في الخطّة المجّانيّة",
              "بلاغة 1 · بلاغة 2 · بلاغة 3 · بلاغة 4",
              "المدخل + المعاني + البيان + البديع",
              "اختبارات ووحدات + 4 شهادات PDF",
              "تقييم AI للكتابات + محادثاتٌ مفتوحة",
              "أساسٌ للتَّرَقّي إلى Advanced لاحقًا",
            ].map((f, i) => (
              <li
                key={i}
                data-testid={`pricing-feature-premium-${i}`}
                style={{
                  display: "flex",
                  gap: 10,
                  marginBottom: 10,
                  fontSize: 14,
                  lineHeight: 1.6,
                }}
              >
                <span style={{ color: "#c79c6a", fontSize: 16 }}>✓</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>

          {err && (
            <div
              data-testid="pricing-error"
              style={{ color: "#ff8b8b", marginBottom: 10, fontSize: 13 }}
            >
              {err}
            </div>
          )}

          <button
            className="btn btn-gold"
            onClick={pilotMode ? () => navigate("/tracks") : handleSubscribe}
            disabled={busy || premiumActive}
            data-testid="pricing-cta-premium"
            style={{
              background: "#c79c6a",
              color: "#1a1530",
              fontWeight: 700,
              padding: 12,
              opacity: premiumActive ? 0.6 : 1,
            }}
          >
            {premiumActive
              ? "✓ مشترك بالفعل"
              : pilotMode
              ? "🚀 ابدأ التعلّم الآن — مفتوح مجّانًا"
              : busy
              ? "..."
              : billing === "monthly"
              ? `اشترك بـ ${premium.amount_sar} ر.س / شهر`
              : `اشترك بـ ${premium.amount_sar} ر.س / سنة`}
          </button>

          {/* Store-required secondary actions */}
          <div
            style={{
              display: "flex",
              gap: 8,
              marginTop: 10,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              className="btn btn-ghost"
              data-testid="pricing-restore-purchases"
              onClick={handleRestorePurchases}
              disabled={busy}
              style={{ fontSize: 12, padding: "6px 12px" }}
            >
              🔄 استرجاع المشتريات
            </button>
            {premiumActive && (
              <button
                type="button"
                className="btn btn-ghost"
                data-testid="pricing-manage-subscription"
                data-provider={(subInfo?.subscription?.provider || "unknown").toLowerCase()}
                onClick={() => openManageSubscription(subInfo?.subscription)}
                style={{ fontSize: 12, padding: "6px 12px" }}
              >
                ⚙️ إدارة الاشتراك
              </button>
            )}
          </div>

          <div
            className="muted"
            style={{ fontSize: 12, marginTop: 10, textAlign: "center" }}
          >
            🚀 متوفّرٌ عبر App Store و Google Play عند إطلاق التطبيق الأصليّ.
          </div>
        </div>

        {/* Institutional */}
        <div
          data-testid="pricing-card-institutional"
          className="panel"
          style={{
            border: "1px solid #2a2640",
            padding: 28,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              color: "#8b6df0",
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: 1,
              marginBottom: 8,
            }}
          >
            المؤسّسيّ
          </div>
          <h3 style={{ fontSize: 22, marginBottom: 18 }}>
            للجامعات والمعاهد والمدارس
          </h3>
          <div style={{ marginBottom: 24 }}>
            <span style={{ fontSize: 28, fontWeight: 700 }}>تسعيرٌ مخصَّص</span>
            <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>
              حسب عدد الطلّاب والاحتياجات
            </div>
          </div>
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              marginBottom: 22,
              flex: 1,
            }}
          >
            {[
              "حسابات متعدّدة للطلّاب والمعلّمين",
              "لوحة تحكّم إداريّة لمتابعة التقدّم",
              "شهادات بشعار المؤسّسة",
              "دمجٌ مع أنظمة LMS (Moodle, Canvas)",
              "محتوًى مُخصَّص لمنهجكم",
              "تدريبٌ لمعلّميكم على المنصّة",
              "تقاريرُ شهريّةٌ مفصَّلة",
              "مدير حساب مُخصَّص",
            ].map((f, i) => (
              <li
                key={i}
                data-testid={`pricing-feature-institutional-${i}`}
                style={{
                  display: "flex",
                  gap: 10,
                  marginBottom: 10,
                  fontSize: 14,
                  lineHeight: 1.6,
                }}
              >
                <span style={{ color: "#8b6df0", fontSize: 16 }}>✓</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <button
            className="btn btn-ghost"
            onClick={() => navigate("/partnerships")}
            data-testid="pricing-cta-institutional"
            style={{
              border: "1px solid #8b6df0",
              color: "#8b6df0",
              fontWeight: 600,
              padding: 12,
            }}
          >
            تواصل معنا
          </button>
        </div>
      </section>

      {/* ==================== EXTRA STANDALONE PRODUCTS ==================== */}
      {(() => {
        // Fully data-driven: any product NOT the primary (balagha_core) is
        // rendered as an extra card. Adding a 4th/5th product in the DB
        // requires ZERO frontend changes.
        const extras = (subInfo?.products || [])
          .filter((p) => p.id !== "balagha_core")
          .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));
        if (!extras.length) return null;
        const cardStyle = (accent) => ({
          padding: "26px 22px",
          border: `1px solid ${accent}55`,
          boxShadow: `0 10px 40px ${accent}22`,
          display: "grid",
          gap: 18,
        });
        return (
          <section
            data-testid="pricing-extra-products"
            style={{
              maxWidth: 1100,
              margin: "40px auto",
              display: "grid",
              gap: 22,
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            }}
          >
            {extras.map((prod) => {
              const monthly = prod.plans.find((p) => p.period === "monthly");
              const yearly = prod.plans.find((p) => p.period === "yearly");
              const accent = prod.accent || "#8b6df0";
              const icon = prod.icon || "✦";
              return (
                <div
                  key={prod.id}
                  data-testid={`pricing-card-${prod.id}`}
                  className="panel"
                  style={cardStyle(accent)}
                >
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 44, marginBottom: 4 }}>{icon}</div>
                    <h3 style={{ margin: 0, fontSize: 20 }}>{prod.name_ar}</h3>
                    <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                      {prod.product_type === "one_time"
                        ? "دفعةٌ واحدة"
                        : prod.product_type === "lifetime"
                        ? "وصولٌ مدى الحياة"
                        : "منتَج اشتراك"}
                    </div>
                  </div>
                  <p
                    className="muted"
                    style={{ textAlign: "center", fontSize: 13, lineHeight: 1.8, margin: 0 }}
                  >
                    {prod.description_ar}
                  </p>
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      flexWrap: "wrap",
                      justifyContent: "center",
                    }}
                  >
                    {[monthly, yearly].filter(Boolean).map((pl) => {
                      const isYearly = pl.period === "yearly";
                      return (
                        <div
                          key={pl.id}
                          data-testid={`${prod.id}-plan-${pl.period}`}
                          style={{
                            padding: "12px 16px",
                            border: `1px solid ${accent}${isYearly ? "" : "55"}`,
                            borderRadius: 12,
                            minWidth: 130,
                            textAlign: "center",
                            background: isYearly ? `${accent}14` : "transparent",
                          }}
                        >
                          <div className="muted" style={{ fontSize: 11 }}>
                            {pl.name_ar}
                            {pl.savings_label_ar && (
                              <span style={{ color: "#2ec48e", marginRight: 4 }}>
                                · {pl.savings_label_ar}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 22, fontWeight: 800, color: accent }}>
                            {pl.amount_sar} ر.س
                          </div>
                          <div className="muted" style={{ fontSize: 10 }}>
                            / {pl.period === "yearly" ? "سنة" : "شهر"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <button
                    className="btn btn-primary"
                    data-testid={`${prod.id}-cta`}
                    onClick={() =>
                      pilotMode
                        ? navigate(
                            prod.unlocks_track_ids?.length === 1
                              ? `/tracks/${prod.unlocks_track_ids[0]}`
                              : "/tracks",
                          )
                        : setErr(
                            "سيكون الاشتراك في هذه الحزمة متاحًا عند إطلاق تطبيق iOS/Android الرسميّ.",
                          )
                    }
                    style={{
                      background: accent,
                      color: "#0e1430",
                      fontWeight: 700,
                      padding: "12px 24px",
                    }}
                  >
                    {pilotMode
                      ? "🚀 مفتوح مجّانًا حاليًّا"
                      : `اشترك في ${prod.name_ar}`}
                  </button>
                  <div
                    className="muted"
                    style={{ fontSize: 11, textAlign: "center" }}
                  >
                    عبر App Store و Google Play عند إطلاق التطبيق الأصليّ.
                  </div>
                </div>
              );
            })}
          </section>
        );
      })()}

      {/* Store availability strip */}
      <section
        style={{
          textAlign: "center",
          padding: "40px 16px 10px",
          maxWidth: 720,
          margin: "0 auto",
        }}
      >
        <div
          className="muted"
          style={{ fontSize: 13, marginBottom: 14, letterSpacing: 0.3 }}
        >
          حمِّل التطبيق الأصليّ عند الإطلاق
        </div>
        <StoreBadges align="center" />
      </section>

      {/* ═════ MANDATORY subscription disclosure (Apple 3.1.2(a) + Play policy) ═════ */}
      <section
        data-testid="pricing-legal-disclosure"
        aria-label="بيان الاشتراك القانونيّ"
        style={{
          maxWidth: 820,
          margin: "40px auto 20px",
          padding: "24px 20px",
          border: "1px solid #2a2640",
          borderRadius: 14,
          background: "rgba(199,156,106,0.04)",
        }}
      >
        <h3
          style={{
            fontSize: 16,
            marginBottom: 14,
            color: "#c79c6a",
            letterSpacing: 0.3,
          }}
        >
          📋 معلومات الاشتراك الرسميّة
        </h3>

        <div style={{ display: "grid", gap: 10, fontSize: 13.5, lineHeight: 1.9 }}>
          <div data-testid="disclosure-plans">
            <strong>الخطط المتوفّرة:</strong> اشتراك شهريّ {monthly.amount_sar} ر.س / شهر،
            اشتراك سنويّ {yearly.amount_sar} ر.س / سنة. الأسعار قد تختلف حسب بلد المتجر.
          </div>
          <div data-testid="disclosure-billing">
            <strong>الفوترة:</strong> يُحصَّل مبلغ الاشتراك عبر حساب Apple ID / Google
            الخاصّ بك عند تأكيد الشراء.
          </div>
          <div data-testid="disclosure-renewal">
            <strong>التجديد التلقائيّ:</strong> يتجدّد الاشتراك تلقائيًّا في نهاية كلّ
            فترة (شهر أو سنة) بنفس السعر ما لم يُلغَ التجديد قبل 24 ساعة من انتهاء
            الفترة الحاليّة. يُحصَّل مبلغ التجديد خلال 24 ساعةً السابقة لبداية الفترة الجديدة.
          </div>
          <div data-testid="disclosure-cancel">
            <strong>الإلغاء:</strong> يمكنك إلغاء التجديد التلقائيّ في أيّ وقتٍ من
            «إعدادات الاشتراكات» في جهازك — على iOS: Settings ← Apple ID ← Subscriptions،
            على Android: Play Store ← Payments &amp; subscriptions ← Subscriptions.
            عند الإلغاء تحتفظ بالوصول حتّى نهاية الفترة المدفوعة.
          </div>
          <div data-testid="disclosure-refund">
            <strong>الاسترداد:</strong> بمجرّد إتمام الشراء لا تُستَرَدّ رسوم الفترة
            الحاليّة، ما لم تُوجِبه سياسات {isIOS ? "Apple" : "المتجر الرسميّ"} أو
            القوانين السارية.
          </div>
          <div data-testid="disclosure-links">
            <strong>الوثائق القانونيّة:</strong>{" "}
            <a href={LEGAL_LINKS.privacy} className="legal-link" data-testid="disclosure-privacy-link">
              سياسة الخصوصيّة
            </a>{" "}
            ·{" "}
            <a href={LEGAL_LINKS.terms} className="legal-link" data-testid="disclosure-terms-link">
              شروط الاستخدام (EULA)
            </a>
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            المُشغِّل الرسميّ: {LEGAL.entity_ar} ({LEGAL.entity_en}) — س.ت {LEGAL.cr_number} — {LEGAL.address_ar}.
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        style={{ maxWidth: 760, margin: "40px auto", padding: "0 16px" }}
        data-testid="pricing-faq"
      >
        <h2 style={{ textAlign: "center", marginBottom: 28, fontSize: 26 }}>
          أسئلة شائعة
        </h2>
        {FAQ.map((item, i) => (
          <details
            key={i}
            data-testid={`pricing-faq-${i}`}
            style={{
              padding: 16,
              borderBottom: "1px solid #2a2640",
              cursor: "pointer",
            }}
          >
            <summary style={{ fontWeight: 600, fontSize: 16, padding: "4px 0" }}>
              {item.q}
            </summary>
            <p
              className="muted"
              style={{ marginTop: 10, lineHeight: 1.9, paddingInlineStart: 20 }}
            >
              {item.a}
            </p>
          </details>
        ))}
      </section>

      {/* Final CTA */}
      <section
        style={{
          textAlign: "center",
          padding: "40px 16px",
          maxWidth: 640,
          margin: "0 auto",
        }}
      >
        <h3 style={{ fontSize: 22, marginBottom: 12 }}>هل أنت مستعدّ؟</h3>
        <p className="muted" style={{ marginBottom: 22, lineHeight: 1.9 }}>
          ابدأ مجّانًا اليوم بمسار «بَلاغة 1» — لا حاجة لبطاقةٍ ائتمانيّة.
          وعندما تكون جاهزًا للمزيد، Premium في انتظارك.
        </p>
        <button
          className="btn btn-primary"
          onClick={() => navigate("/tracks")}
          data-testid="pricing-final-cta"
          style={{ padding: "14px 32px", fontSize: 16 }}
        >
          استكشف المسارات →
        </button>
      </section>
    </div>
  );
}
